import { useRef } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'motion/react';
import { ArrowUpRight } from 'lucide-react';

interface HeroProjectShowcaseProps {
  locale: 'sv' | 'en';
  onOpenFampo: () => void;
  onOpenAssociation?: () => void;
}

export function HeroProjectShowcase({ locale, onOpenFampo, onOpenAssociation }: HeroProjectShowcaseProps) {
  const ref = useRef<HTMLDivElement>(null);
  const px = useMotionValue(0);
  const py = useMotionValue(0);
  const sx = useSpring(px, { stiffness: 120, damping: 24, mass: 0.7 });
  const sy = useSpring(py, { stiffness: 120, damping: 24, mass: 0.7 });
  const rotateX = useTransform(sy, [-1, 1], [4.5, -4.5]);
  const rotateY = useTransform(sx, [-1, 1], [-6, 6]);

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    px.set(((event.clientX - rect.left) / rect.width - 0.5) * 2);
    py.set(((event.clientY - rect.top) / rect.height - 0.5) * 2);
  };

  const reset = () => {
    px.set(0);
    py.set(0);
  };

  const labels = locale === 'sv'
    ? {
        products: 'Produkter under utveckling',
        fampoStatus: 'Pre-launch · Android',
        associationStatus: 'Aktiv utveckling',
        open: 'Utforska',
        note: 'Keevo bygger egna produkter parallellt med utvalda projekt.',
        overview: 'Översikt',
      }
    : {
        products: 'Products in development',
        fampoStatus: 'Pre-launch · Android',
        associationStatus: 'Active development',
        open: 'Explore',
        note: 'Keevo builds its own products alongside selected projects.',
        overview: 'Overview',
      };

  return (
    <motion.div
      ref={ref}
      className="hero-project-showcase"
      onPointerMove={handlePointerMove}
      onPointerLeave={reset}
      style={{ rotateX, rotateY, transformPerspective: 1200 }}
    >
      <div className="hero-project-showcase__wash" aria-hidden="true" />
      <div className="hero-project-showcase__grid" aria-hidden="true" />

      <div className="hero-project-showcase__topline">
        <span>{labels.products}</span>
        <i />
      </div>

      <motion.button
        type="button"
        className="hero-project hero-project--fampo"
        onClick={onOpenFampo}
        whileHover={{ y: -7, rotate: -1.1 }}
        transition={{ type: 'spring', stiffness: 240, damping: 21 }}
      >
        <div className="hero-project__meta">
          <div>
            <span>01</span>
            <strong>FamPo</strong>
          </div>
          <small>{labels.fampoStatus}</small>
        </div>
        <div className="hero-fampo-phone" aria-hidden="true">
          <div className="hero-fampo-phone__speaker" />
          <div className="hero-fampo-phone__head"><b>FamPo</b><i>FP</i></div>
          <div className="hero-fampo-phone__points"><strong>240</strong><span>FP</span></div>
          <div className="hero-fampo-phone__task"><b>✓</b><span /></div>
          <div className="hero-fampo-phone__task"><b>+</b><span /></div>
          <div className="hero-fampo-phone__task"><b>✓</b><span /></div>
        </div>
        <span className="hero-project__open">{labels.open}<ArrowUpRight size={15} /></span>
      </motion.button>

      <motion.button
        type="button"
        className="hero-project hero-project--association"
        onClick={onOpenAssociation}
        whileHover={{ y: -8, rotate: 1.1 }}
        transition={{ type: 'spring', stiffness: 240, damping: 21 }}
      >
        <div className="hero-project__meta">
          <div>
            <span>02</span>
            <strong>Din Förening</strong>
          </div>
          <small>{labels.associationStatus}</small>
        </div>
        <div className="hero-association-ui" aria-hidden="true">
          <div className="hero-association-ui__nav"><i /><i /><i /></div>
          <div className="hero-association-ui__body">
            <div className="hero-association-ui__metric"><small>{labels.overview}</small><strong>•••</strong></div>
            <div className="hero-association-ui__chart"><i /><i /><i /><i /><i /></div>
            <div className="hero-association-ui__rows"><i /><i /><i /></div>
          </div>
        </div>
        <span className="hero-project__open">{labels.open}<ArrowUpRight size={15} /></span>
      </motion.button>

      <div className="hero-project-showcase__note">{labels.note}</div>
    </motion.div>
  );
}
